import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import LecturerLayout from '../../components/LecturerLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import HodThread, {
  HOD_STATUS_META,
  HodAvatar,
  HodStatusBadge,
  hodFullDate,
  hodTimeAgo,
} from '../../components/HodThread';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'in process', label: 'In process' },
  { key: 'solved', label: 'Solved' },
];

const STEPS = ['pending', 'in process', 'solved'];

const toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2200, timerProgressBar: true });

// Pending -> In process -> Solved, so the lecturer can see at a glance how
// far their message has got.
function ProgressTracker({ status }) {
  const current = STEPS.indexOf(status);
  return (
    <div className="flex items-center">
      {STEPS.map((step, idx) => {
        const reached = idx <= current;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                  reached ? `${HOD_STATUS_META[step].dot} text-white` : 'bg-gray-200 text-gray-400'
                }`}
              >
                {reached ? '✓' : idx + 1}
              </div>
              <span className={`text-[11px] font-semibold ${reached ? 'text-gray-800' : 'text-gray-400'}`}>
                {HOD_STATUS_META[step].label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`mx-2 mb-4 h-0.5 w-10 sm:w-16 ${idx < current ? 'bg-gray-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function NotifyHod() {
  const staff = AuthService.getUser()?.superAdmin?.staff || {};

  const [notifications, setNotifications] = useState([]);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/hod-notifications');
      setNotifications(res.data?.notifications || []);
      setCurrentStaffId(res.data?.currentStaffId || null);
    } catch (err) {
      if (!silent) Swal.fire('Error', err?.response?.data?.message || 'Failed to load your messages.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Quietly pick up the HOD's replies without a page refresh.
  useEffect(() => {
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [load]);

  const replaceNotification = (updated) => {
    setNotifications((prev) => {
      const exists = prev.some((n) => n.notificationId === updated.notificationId);
      const next = exists
        ? prev.map((n) => (n.notificationId === updated.notificationId ? updated : n))
        : [updated, ...prev];
      return next.sort((a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt));
    });
  };

  const counts = useMemo(() => {
    const c = { all: notifications.length, pending: 0, 'in process': 0, solved: 0 };
    notifications.forEach((n) => { c[n.status] = (c[n.status] || 0) + 1; });
    return c;
  }, [notifications]);

  const visible = useMemo(
    () => (filter === 'all' ? notifications : notifications.filter((n) => n.status === filter)),
    [notifications, filter]
  );

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim() || sending) return;
    setSending(true);
    try {
      const res = await axiosInstance.post('/admin/hod-notifications', { subject, message });
      replaceNotification(res.data.notification);
      setSubject('');
      setMessage('');
      setFilter('all');
      setExpandedId(res.data.notification.notificationId);
      toast.fire({ icon: 'success', title: 'Sent to the HOD' });
    } catch (err) {
      Swal.fire('Could not send', err?.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setSending(false);
    }
  };

  const toggle = async (n) => {
    const opening = expandedId !== n.notificationId;
    setExpandedId(opening ? n.notificationId : null);
    if (opening && n.unread) {
      setNotifications((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, unread: false } : x)));
      axiosInstance.post(`/admin/hod-notifications/${n.notificationId}/seen`).catch(() => {});
    }
  };

  const postComment = async (id, comment) => {
    try {
      const res = await axiosInstance.post(`/admin/hod-notifications/${id}/comments`, { comment });
      replaceNotification(res.data.notification);
      return true;
    } catch (err) {
      Swal.fire('Could not post', err?.response?.data?.message || 'Please try again.', 'error');
      return false;
    }
  };

  const deleteComment = async (id, commentId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this comment?',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`/admin/hod-notifications/${id}/comments/${commentId}`);
      replaceNotification(res.data.notification);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete the comment.', 'error');
    }
  };

  return (
    <LecturerLayout title="Notify HOD">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 p-6 text-white shadow-sm">
          <h1 className="text-2xl font-bold">Notify HOD</h1>
          <p className="mt-1 text-sm text-emerald-50">
            Send a message to the Head of Department and follow it here — you'll see the status and every reply, and you can reply back.
          </p>
        </div>

        <form onSubmit={send} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <HodAvatar person={{ staffId: staff.staffId, name: staff.name, imagePath: staff.imagePath }} size="h-10 w-10" />
            <div>
              <p className="text-sm font-semibold text-gray-900">{staff.name || 'You'}</p>
              <p className="text-xs text-gray-500">New message to the HOD</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700" htmlFor="hod-subject">Subject</label>
              <span className="text-[11px] text-gray-400">{subject.length}/255</span>
            </div>
            <input
              id="hod-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              placeholder="e.g. Projector not working in Hall 02"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700" htmlFor="hod-message">Message</label>
              <span className="text-[11px] text-gray-400">{message.length}/5000</span>
            </div>
            <textarea
              id="hod-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              rows={4}
              placeholder="Describe the issue or request in as much detail as you can…"
              className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="mt-4 flex items-center justify-end">
            <button
              type="submit"
              disabled={sending || !subject.trim() || !message.trim()}
              className="rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {sending ? 'Sending…' : 'Send to HOD'}
            </button>
          </div>
        </form>

        <div>
          <div className="mb-3 flex flex-wrap items-center gap-1 rounded-lg bg-gray-100 p-1 text-sm font-semibold">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                className={`rounded-md px-3.5 py-1.5 transition ${filter === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {tab.label} <span className="ml-0.5 text-xs text-gray-400">({counts[tab.key] || 0})</span>
              </button>
            ))}
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-gray-400">Loading your messages…</p>
          ) : visible.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
              <p className="text-sm font-semibold text-gray-700">
                {notifications.length === 0 ? "You haven't sent any messages yet." : 'Nothing in this status.'}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {notifications.length === 0 ? 'Use the form above to notify the HOD.' : 'Try another tab.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map((n) => {
                const meta = HOD_STATUS_META[n.status] || HOD_STATUS_META.pending;
                const open = expandedId === n.notificationId;
                return (
                  <div key={n.notificationId} className={`overflow-hidden rounded-xl border border-gray-200 border-l-4 ${meta.accent} bg-white shadow-sm`}>
                    <button
                      type="button"
                      onClick={() => toggle(n)}
                      className="flex w-full items-start justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-sm font-bold text-gray-900">{n.subject}</h3>
                          <HodStatusBadge status={n.status} />
                          {n.unread && (
                            <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">New reply</span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500" title={hodFullDate(n.createdAt)}>
                          Sent {hodTimeAgo(n.createdAt)} · {n.comments.length} {n.comments.length === 1 ? 'reply' : 'replies'}
                        </p>
                      </div>
                      <svg className={`mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {open && (
                      <div className="space-y-4 border-t border-gray-100 px-5 pb-5 pt-4">
                        <ProgressTracker status={n.status} />

                        <div className="flex items-start gap-3">
                          <HodAvatar person={n.creator} />
                          <div className="min-w-0 flex-1 rounded-2xl bg-gray-50 px-4 py-3">
                            <p className="text-xs font-semibold text-gray-900">
                              You <span className="font-normal text-gray-400">· {hodFullDate(n.createdAt)}</span>
                            </p>
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800">{n.message}</p>
                          </div>
                        </div>

                        <HodThread
                          notification={n}
                          currentStaffId={currentStaffId}
                          viewerIsHod={false}
                          onComment={postComment}
                          onDeleteComment={deleteComment}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </LecturerLayout>
  );
}
